import { APP_BASE_HREF, DOCUMENT, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  collectAttributionForUrlUpdate,
  mergeAttributionIntoLocationArgs,
} from './attribution-query';

/**
 * Global: every Angular pushState / replaceState keeps ads click + campaign
 * params (gclid, fbclid, utm_*, *clid, …) in the address bar.
 * Canonical / og:url stay clean via CanonicalService.sanitizeCanonicalUrl.
 */
@Injectable()
export class AttributionPreservingPathLocationStrategy extends PathLocationStrategy {
  constructor(
    platformLocation: PlatformLocation,
    @Optional() @Inject(APP_BASE_HREF) href?: string,
    @Optional() @Inject(DOCUMENT) private readonly document?: Document,
  ) {
    super(platformLocation, href);
    // Snapshot landing-page ads params into session as soon as the app boots.
    try {
      const search = this.document?.defaultView?.location?.search;
      if (search) {
        collectAttributionForUrlUpdate(search);
      }
    } catch {
      /* SSR */
    }
  }

  override pushState(state: any, title: string, url: string, queryParams: string): void {
    const [nextUrl, nextQuery] = this.withAttribution(url, queryParams);
    super.pushState(state, title, nextUrl, nextQuery);
  }

  override replaceState(state: any, title: string, url: string, queryParams: string): void {
    const [nextUrl, nextQuery] = this.withAttribution(url, queryParams);
    super.replaceState(state, title, nextUrl, nextQuery);
  }

  private withAttribution(url: string, queryParams: string): [string, string] {
    return mergeAttributionIntoLocationArgs(url, queryParams, this.currentSearch());
  }

  private currentSearch(): string {
    try {
      const search = this.document?.defaultView?.location?.search;
      if (search) {
        return search;
      }
    } catch {
      /* SSR / restricted */
    }
    try {
      const path = this.path() || '';
      const q = path.indexOf('?');
      if (q >= 0) {
        return path.slice(q);
      }
    } catch {
      /* ignore */
    }
    return '';
  }
}
